import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchProducts, createProduct, deleteProduct, deleteGreyCards } from '../utils/api';

/**
 * Custom hook for catalog products management, real-time client-side search,
 * dual-range 0-5M VND price filtering, category/platform switching, and CRUD operations.
 */
export function useProducts(filters = {}, isAdmin = false) {
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    category = 'all',
    platform = 'all',
    searchQuery = '',
    minPrice = 0,
    maxPrice = 5000000,
    sortBy = 'default'
  } = filters;

  // Load all products initially
  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchProducts();
      setAllProducts(data.products || []);
    } catch (err) {
      setError(err.message || 'Lỗi khi tải sản phẩm');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Aggregate catalog counts
  const stats = useMemo(() => {
    const total = allProducts.length;
    const grey = allProducts.filter(p => p.is_grey === 1).length;
    const active = total - grey;
    return { total, active, grey };
  }, [allProducts]);

  // Real-time client-side filtering (<5ms)
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    // RULE: Regular visitors NEVER see grey cards!
    // Grey cards only appear in Admin tab
    if (!isAdmin) {
      result = result.filter(p => p.is_grey !== 1);
    }

    // 1. Category filter
    if (category && category !== 'all') {
      result = result.filter(p => {
        if (p.category_slug && p.category_slug.toLowerCase() === category.toLowerCase()) return true;
        if (p.category_name && p.category_name.toLowerCase().includes(category.toLowerCase())) return true;
        if (String(p.category_id) === String(category)) return true;
        return false;
      });
    }

    // 2. Platform filter
    if (platform && platform !== 'all') {
      result = result.filter(p => p.platform === platform);
    }

    // 3. Dual-Range price filter (Start point & End point in VND)
    if (minPrice > 0) {
      result = result.filter(p => (p.price_vnd || 0) >= minPrice);
    }
    if (maxPrice < 5000000) {
      result = result.filter(p => (p.price_vnd || 0) <= maxPrice);
    }

    // 4. Search query (Title, tags, description)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const tokens = q.split(/\s+/).filter(Boolean);
      result = result.filter(p => {
        const titleMatch = p.title && p.title.toLowerCase();
        const descMatch = p.description && p.description.toLowerCase();
        const tagMatch = Array.isArray(p.tags) ? p.tags.join(' ').toLowerCase() : (p.tags || '').toLowerCase();
        const catMatch = (p.category_name || '').toLowerCase();

        return tokens.every(token =>
          (titleMatch && titleMatch.includes(token)) ||
          (descMatch && descMatch.includes(token)) ||
          tagMatch.includes(token) ||
          catMatch.includes(token)
        );
      });
    }

    return result;
  }, [allProducts, isAdmin, category, platform, minPrice, maxPrice, searchQuery]);

  // Actions
  const addProduct = useCallback(async (productData) => {
    try {
      const res = await createProduct(productData);
      if (res.success && res.product) {
        setAllProducts(prev => [res.product, ...prev]);
      }
      return res;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const removeProduct = useCallback(async (id) => {
    try {
      const res = await deleteProduct(id);
      if (res.success) {
        setAllProducts(prev => prev.filter(p => p.id !== id));
      }
      return res;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const purgeGreyCards = useCallback(async () => {
    try {
      const res = await deleteGreyCards();
      if (res.success) {
        setAllProducts(prev => prev.filter(p => p.is_grey !== 1));
      }
      return res;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const deleteSingleGreyCard = useCallback(async (id) => {
    return removeProduct(id);
  }, [removeProduct]);

  return {
    products: filteredProducts,
    allProducts,
    stats,
    isLoading,
    error,
    reloadProducts: loadProducts,
    addProduct,
    removeProduct,
    purgeGreyCards,
    deleteSingleGreyCard
  };
}
